import PropTypes from "prop-types";

import AssignmentEmbedStudentContainer from "@/assignments/AssignmentEmbedContainer/AssignmentEmbedStudentContainer";
import ContentWarning from "@/components/ContentWarning";
import contentTypes from "@/view/constants/contentTypes";
import SENSITIVE_CONTENT from "@/view/constants/sensitiveContent";
import EditContentTools from "@/view/EditContentTools";
import useAssignmentReducerContext from "@/view/utils/useAssignmentReducerContext";
import useStudentPreviewContext from "@/view/utils/useStudentPreviewContext";
import useViewPageContentContext from "@/view/ViewPageContent/useViewPageContentContext";
import GeniusVideo from "@/view/GeniusContent/GeniusVideo";
import usePresentModeContext from "../utils/usePresentModeContext";
import getContentCategory from "../utils/getContentCategory";
import ContentDisplayTitle from "../ContentDisplay/ContentDisplayTitle";
import ContentDisplayEyebrow from "../ContentDisplay/ContentDisplayEyebrow";
import DecodableBadge from "./DecodableBadge";
import LiteOrPremiumBadge from "./LiteOrPremiumBadge";
import {
  informationViewAsStudentCss,
  instructionsViewAsStudentCss,
  rootCss,
  rootViewAsStudentCss,
} from "./ContentHeader.style";

const propTypes = {
  assignment: PropTypes.object,
  contentData: PropTypes.shape({
    id: PropTypes.string,
    content_type: PropTypes.string,
    data: PropTypes.shape({
      content_warning: PropTypes.string,
      name: PropTypes.string,
      is_lite: PropTypes.bool.isRequired,
      date_lite_product_grant_expires: PropTypes.string,
    }),
  }),
  contentMetadata: PropTypes.array,
  currentLevel: PropTypes.object,
  publishedUserAssignments: PropTypes.array,
  userProperties: PropTypes.shape({
    isLiteUser: PropTypes.bool,
    hasFeature: PropTypes.func,
    isAuthenticated: PropTypes.bool,
    isStudent: PropTypes.bool,
  }),
};

const unauthPropTypes = {
  currentLevel: PropTypes.object,
};

const ContentHeader = ({
  assignment,
  contentData,
  contentMetadata,
  currentLevel,
  publishedUserAssignments,
  userProperties,
}) => {
  const { isVideoPresentModeActive } = usePresentModeContext();
  const { isStudentPreviewOn } = useStudentPreviewContext();
  const { updatedAssignment } = useAssignmentReducerContext();

  if (isVideoPresentModeActive) return null;

  const { data } = contentData;
  const {
    is_lite: isLiteContent = false,
    date_lite_product_grant_expires: expireDateUtc = null,
  } = data;

  const { hasFeature, isLiteUser, isAuthenticated, isStudent } = userProperties;

  const contentIsVideo =
    contentData.content_type === contentTypes.INTERACTIVE_VIDEO;
  let title = contentIsVideo ? data?.name : currentLevel?.title;
  if (contentData.content_type === contentTypes.GENERATION_GENIUS_VIDEO) {
    title = data?.title;
  }

  const renderContentWarning = () => {
    if (!isAuthenticated && !contentMetadata) return null;
    const sensitiveContentTag =
      contentMetadata &&
      contentMetadata.find((tag) => tag?.data?.name === SENSITIVE_CONTENT);

    return sensitiveContentTag && !isStudent ? (
      <ContentWarning contentWarning={data.content_warning} />
    ) : null;
  };

  const renderEyebrow = () => {
    if (!contentMetadata) return null;
    const label = getContentCategory(contentData, contentMetadata);
    return <ContentDisplayEyebrow eyebrow={label} />;
  };

  const renderEditLink = () => {
    const canViewEditingTools = hasFeature("can_view_editing_tools");
    if (!canViewEditingTools) return null;

    return (
      <EditContentTools
        contentId={contentData.id}
        isArticle={!contentIsVideo}
      />
    );
  };

  const renderLiteOrPremiumBadge = () => {
    // Newsela professional development videos are available to Lite users
    if (contentIsVideo && isLiteContent) return null;
    return (
      <LiteOrPremiumBadge
        isLiteUser={isLiteUser}
        isLiteContent={isLiteContent}
        expireDateUtc={expireDateUtc}
      />
    );
  };
  const renderDecodableBadge = () => {
    return (
      <DecodableBadge
        contentMetadata={contentMetadata}
        isStudentMode={isStudent || isStudentPreviewOn}
      />
    );
  };

  return (
    <div css={rootCss}>
      {isStudentPreviewOn ? (
        <AssignmentEmbedStudentContainer
          __cssFor={{
            information:
              updatedAssignment?.instructions === ""
                ? null
                : informationViewAsStudentCss,
            instructions: instructionsViewAsStudentCss,
            root: rootViewAsStudentCss,
          }}
          assignment={isStudentPreviewOn ? updatedAssignment : assignment}
          currentContentLevelId={currentLevel?.id}
          hasMultipleAssignments={publishedUserAssignments.length > 1}
        />
      ) : null}
      {renderEditLink()}
      {renderContentWarning()}
      {renderDecodableBadge()}
      {renderLiteOrPremiumBadge()}
      {renderEyebrow()}
      <ContentDisplayTitle currentLevel={currentLevel} title={title} />
    </div>
  );
};

const Unauth = ({ currentLevel }) => {
  const { content } = useViewPageContentContext();
  const contentData = content.data;
  const { wistia_id: wistiaId } = contentData;
  const isGenerationGeniusVideo =
    content.content_type === contentTypes.GENERATION_GENIUS_VIDEO;
  return (
    <div css={rootCss}>
      {isGenerationGeniusVideo ? (
        <GeniusVideo wistiaId={wistiaId} content={content} isPreview />
      ) : (
        <ContentDisplayTitle.Unauth
          currentLevel={currentLevel}
          title={currentLevel?.title}
        />
      )}
    </div>
  );
};

ContentHeader.propTypes = propTypes;
Unauth.propTypes = unauthPropTypes;

ContentHeader.Unauth = Unauth;

export default ContentHeader;
